import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const STEPS = [1, 1.25, 1.5, 2, 2.5, 3];
const MIN = 1, MAX = 3;
const clamp = (v) => Math.min(MAX, Math.max(MIN, v));
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

// Two-finger pinch on the viewport; keeps the point between the fingers in place.
const usePinch = (vp, z, setZ, anchor) => {
  const zr = useRef(z);
  zr.current = z;
  useEffect(() => {
    const el = vp.current;
    if (!el) return;
    let start = null;
    const onStart = (e) => {
      if (e.touches.length === 2) start = { d: dist(e.touches), z: zr.current };
    };
    const onMove = (e) => {
      if (!start || e.touches.length !== 2) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left;
      const my = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top;
      const next = clamp(start.z * (dist(e.touches) / start.d));
      anchor.current = { mx, my, x: (el.scrollLeft + mx) / zr.current, y: (el.scrollTop + my) / zr.current };
      setZ(next);
    };
    const onEnd = (e) => { if (e.touches.length < 2) start = null; };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [vp, setZ, anchor]);
};

// Zooms the live preview: buttons or pinch; scroll / swipe to pan when zoomed in.
export const PreviewZoom = ({ className, children, prefix = "preview" }) => {
  const [z, setZ] = useState(1);
  const vp = useRef(null);
  const anchor = useRef(null);
  usePinch(vp, z, setZ, anchor);

  useLayoutEffect(() => {
    const el = vp.current;
    if (!el) return;
    const a = anchor.current;
    anchor.current = null;
    if (a) el.scrollTo(a.x * z - a.mx, a.y * z - a.my);
    else el.scrollTo((el.scrollWidth - el.clientWidth) / 2, (el.scrollHeight - el.clientHeight) / 2);
  }, [z]);

  const step = (dir) => setZ(dir > 0 ? STEPS.find((s) => s > z + 0.01) ?? MAX : [...STEPS].reverse().find((s) => s < z - 0.01) ?? MIN);
  const Btn = ({ onClick, disabled, label, testId, children: c }) => (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} data-testid={testId}
      className="flex h-8 w-8 items-center justify-center bg-white text-slate-900 transition-colors hover:bg-lime-200 disabled:opacity-40">{c}</button>
  );
  return (
    <div className={`relative flex flex-col ${className}`}>
      <div ref={vp} className="min-h-0 w-full flex-1 overflow-auto" style={{ touchAction: "pan-x pan-y" }} data-testid={`${prefix}-zoom-viewport`}>
        <div className="flex items-center justify-center" style={{ width: `${z * 100}%`, height: `${z * 100}%`, minWidth: "100%", minHeight: "100%" }}>
          {children}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-between gap-2 bg-white/80 px-1 py-1 lg:pointer-events-none lg:absolute lg:bottom-2 lg:right-2 lg:bg-transparent lg:p-0">
        <span className="text-[10px] font-semibold text-slate-600 lg:hidden">Pinch to zoom</span>
        <div className="pointer-events-auto z-10 flex items-center border-2 border-slate-900 bg-white shadow-[2px_2px_0_0_#0f172a]" data-testid={`${prefix}-zoom-controls`}>
          <Btn onClick={() => step(-1)} disabled={z <= MIN} label="Zoom out" testId={`${prefix}-zoom-out`}><ZoomOut className="h-4 w-4" /></Btn>
          <span className="font-mono-cc w-12 text-center text-xs font-bold" data-testid={`${prefix}-zoom-level`}>{Math.round(z * 100)}%</span>
          <Btn onClick={() => step(1)} disabled={z >= MAX} label="Zoom in" testId={`${prefix}-zoom-in`}><ZoomIn className="h-4 w-4" /></Btn>
          <Btn onClick={() => setZ(1)} disabled={z <= MIN} label="Fit to view" testId={`${prefix}-zoom-fit`}><Maximize2 className="h-3.5 w-3.5" /></Btn>
        </div>
      </div>
    </div>
  );
};
